import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { allCostColumns, allToggleColumns, allRevenueColumns, budgetSectionColumns, BudgetSectionGridData, GridCol, PaymentGridData, BudgetSectionColumnIds } from '../../helpers/grid-helpers';
import { orderListByIds } from '../../helpers/helpers';
import { AddItemRowData, BudgetSchedule, BudgetSection, createDefaultLineItem, LineItem, ManageBudgetLinkOption, SaveBudgetCellData, SectionSettings } from '../../store/reselectors/budget.data';
import { BudgetSettingsComponent, BudgetSettingsDialogData } from './budget-settings.component';
import { MatDialog } from '@angular/material/dialog';
import { DropdownOption } from 'globals/store/programs.data';
import { OnChanges } from '@angular/core';
import { BudgetGridComponent } from './budget-grid.component';
import { JoiInputComponent } from 'src/app/components/joi-input/joi-input.component';
import { accountBudgetRateKeys, JoiAccount } from 'globals/store/accounts.data';
import { jsonCopyObject, setUnion } from 'globals/helpers/helpers';
import { ActionButtonComponent } from 'src/app/components/nav-bar/action-button.component';
import { DropdownComponent } from 'src/app/components/misc/dropdown.component';
import { UpDownArrowsComponent } from 'src/app/components/misc/up-down-arrows.component';
import { DeleteButtonComponent } from 'src/app/components/buttons/delete-button.component';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { ExpandingWrapperComponent } from 'src/app/components/wrappers/expanding-wrapper.component';
import { FileHandlerComponent } from 'src/app/components/file-handler/file-handler.component';
import { BudgetScheduleComponent } from './budget-schedule.component';
import { PrettyNumbersPipe } from 'src/app/services/pipes/prettyNumbers.pipe';

@Component({
    selector: 'budget-section',
    templateUrl: './budget-section.html',
    styleUrls: ['./budget-section.scss', './budget-totals.scss'],
    standalone: true,
    imports: [JoiInputComponent, ActionButtonComponent, PrettyNumbersPipe, DropdownComponent, UpDownArrowsComponent, DeleteButtonComponent, BudgetGridComponent, MatButtonModule, CommonModule, ExpandingWrapperComponent, FileHandlerComponent, FileHandlerComponent, BudgetScheduleComponent],
})
export class BudgetSectionComponent implements OnInit, OnChanges {
  @Input() section: BudgetSection
  @Input() hasChanges: boolean
  @Input() public = false
  @Input() hideTitle = false
  @Input() viewMode = false

  @Input() allowSectionOrdering: boolean = false // allows reordering even when in "read mode" (set by readOnly)

  @Input() gridData: BudgetSectionGridData[]
  @Input() scheduleGridData: PaymentGridData[]
  @Input() budgetCurrency: string
  @Input() readOnly = false
  @Input() showAllColumns
  @Input() isRevenue = true
  @Input() isCostTasks = false
  @Input() isCostAssets = false
  @Input() isLinkedSection = false
  @Input() hideQuantityUpdateColumn = true
  @Input() hideActionUpdateColumn = true

  @Input() isToggleSection = false
  @Input() lockQuantityColumn = false

  @Input() first = false
  @Input() last = false
  @Input() single = false
  @Input() canAddLines = true
  @Input() canDeleteLines = true

  @Output() moveUp: EventEmitter<void> = new EventEmitter()
  @Output() moveDown: EventEmitter<void> = new EventEmitter()
  @Output() deleteSection: EventEmitter<void> = new EventEmitter()
  @Output() viewUpdates: EventEmitter<void> = new EventEmitter()
  @Input() updatesLabel = 'UPDATES AVAILABLE'

  @Input() linkDropdownOptions: DropdownOption<ManageBudgetLinkOption>[] 
  @Output() linkDropdownSelection: EventEmitter<ManageBudgetLinkOption> = new EventEmitter()
  @Output() addItem: EventEmitter<AddItemRowData<BudgetSectionGridData>> = new EventEmitter()
  @Input() canAddItems = false
  @Input() fullWidth = false

  deleteTitle = 'Delete Section'
  deleteText = 'Are you sure you want to delete this section from the budget?'

  @Output() saveSection: EventEmitter<BudgetSection> = new EventEmitter()
  @Output() changeSectionSettingDefaults: EventEmitter<Partial<SectionSettings>> = new EventEmitter()
  @Output() nameChange: EventEmitter<string> = new EventEmitter()
  @Output() quantityUpdated: EventEmitter<LineItem> = new EventEmitter

  usedColumns: GridCol[]
  hiddenColumns: Set<keyof BudgetSectionColumnIds> = new Set()
  @ViewChild(BudgetGridComponent) budgetGrid: BudgetGridComponent<BudgetSectionGridData>
  @ViewChild('sectionTitle') sectionTitle: JoiInputComponent
  account: JoiAccount

  constructor(private dialog: MatDialog, private changeDetectorRef: ChangeDetectorRef) {}

  focusSectionName() {
    this.sectionTitle.manualFocus()
  }

  ngOnInit(): void {
    this.setColumns()
  }

  ngOnChanges() {
    this.setColumns()
  }

  async checkUpdates(event) {
    this.viewUpdates.emit()
  }

  linkDropdownSelect(id: string) {
    this.linkDropdownSelection.emit(id as ManageBudgetLinkOption)
  }
  

  setColumns() {
    this.hiddenColumns = new Set()
    if (this.hideActionUpdateColumn) this.hiddenColumns.add('actionButton')
    if (this.hideQuantityUpdateColumn) this.hiddenColumns.add('qtyUpdated')
    if (this.isRevenue || this.public || !this.section.settings.useUnitRate) this.hiddenColumns.add('linkedUnitRate')
    if (this.isRevenue || this.public || this.readOnly || !this.section.settings.useMarkup) this.hiddenColumns.add('markup')

    const columnTemplate = this.isToggleSection ? allToggleColumns : this.isRevenue ? allRevenueColumns : allCostColumns

    this.usedColumns = jsonCopyObject(columnTemplate) // COPY columns so can modify below without bugs
    if (this.lockQuantityColumn) this.usedColumns.find(c => c.id === budgetSectionColumns.qty).uneditable = true

    const sectionCurrency = this.section.settings.currency
    const secondCurrency = sectionCurrency && sectionCurrency !== this.budgetCurrency

    const convertedRateColumn = this.usedColumns.find(c => c.id === budgetSectionColumns.convertedRate)
    const rateColumn = this.usedColumns.find(c => c.id === budgetSectionColumns.rate)
    const linkedUnitRateColumn = this.usedColumns.find(c => c.id === budgetSectionColumns.linkedUnitRate)

    // console.log({ secondCurrency, showAllColumns: this.showAllColumns, this: this })

    if (secondCurrency) {
      convertedRateColumn.title = `${sectionCurrency}\n${convertedRateColumn.title}`
      rateColumn.title = `${this.budgetCurrency}\n${rateColumn.title}`
      rateColumn.uneditable = true
      linkedUnitRateColumn.title = `${this.budgetCurrency}\n${linkedUnitRateColumn.title}`   
    } else {
      this.hiddenColumns.add(budgetSectionColumns.convertedRate)
    }

    if (this.readOnly && !this.showAllColumns) {
      this.hiddenColumns = setUnion(this.hiddenColumns, new Set([ budgetSectionColumns.status, budgetSectionColumns.discount, budgetSectionColumns.fees, budgetSectionColumns.tax, budgetSectionColumns.markup ]))
    } else {
      const { useFees, useDiscount, useTax, useMarkup } = this.section.settings
      if (!secondCurrency) this.hiddenColumns.add(budgetSectionColumns.convertedRate)
      if (!useFees) this.hiddenColumns.add(budgetSectionColumns.fees)
      if (!useTax) this.hiddenColumns.add(budgetSectionColumns.tax)
      if (!useDiscount) this.hiddenColumns.add(budgetSectionColumns.discount)
      if (!useMarkup) this.hiddenColumns.add(budgetSectionColumns.markup)
    }

    if (this.isLinkedSection) { // this is for linked cost sections that have not had a markup applied 

    }

  }

  saveSchedule(schedule: BudgetSchedule) {
    this.section.schedule = schedule
    this.saveSection.emit(this.section)
  }

  saveFiles(files) {
    this.section.files = files
    this.saveSection.emit(this.section)
  }

  saveRows(rows: BudgetSectionGridData[]) {
    this.section.lineItems = orderListByIds(this.section.lineItems, rows.map(r => r.id))
    this.saveSection.emit(this.section)
  }

  saveCell(data: SaveBudgetCellData<BudgetSectionGridData>) {
    const { column, saveData, row } = data
    const lineItem = this.section.lineItems.find(r => r.id === row.id)
    switch (column.id) {
      case budgetSectionColumns.actionButton: {
        switch (row.actionButton) {
          case 'UPDATE':
            lineItem.quantity = row.qtyUpdated.amount
            break
          case 'DELETE':
            this.budgetGrid.deleteRow(this.section.lineItems.findIndex(r => r.id === row.id))
            break
        }
        break
      }
      case budgetSectionColumns.show:
        lineItem.show = !lineItem.show
        break
      case budgetSectionColumns.item:
        lineItem.item = row.item.text
        console.log({ lineItem, row })
        break
      case budgetSectionColumns.description:
        lineItem.description = row.description.text
        break
      case budgetSectionColumns.discount:
        lineItem.discount = !lineItem.discount
        break
      case budgetSectionColumns.tax:
        lineItem.tax = !lineItem.tax
        break
      case budgetSectionColumns.fees:
        lineItem.fees = !lineItem.fees
        break
      case budgetSectionColumns.qty:
        lineItem.quantity = saveData
        this.quantityUpdated.emit(lineItem)
        break
      case budgetSectionColumns.rate:
      case budgetSectionColumns.convertedRate:
        lineItem.unitCost = saveData
        break
      case budgetSectionColumns.linkedUnitRate:
        lineItem.unitRate = saveData
        break
      case budgetSectionColumns.recurrence:
        lineItem.recurrence = saveData
        break
      case budgetSectionColumns.status:
        lineItem.status = saveData.id
        break
      case budgetSectionColumns.markup:
        if (lineItem.markup && lineItem.itemId) { // markup being turned off for line item from item template
          lineItem.unitRate = lineItem.itemRate || 0
        }
        lineItem.markup = !lineItem.markup
        break
      default:
        console.log(`Missing handling for ${column.id}`, data)
        return
    }
    this.saveSection.emit(this.section)
  }

  updateConversionRate() {
    let amount: number
    try {
      amount = +this.section.settings.conversionRate
      if (isNaN(amount)) amount = 1
    } catch {
      amount = 1
    }
    this.section.settings.conversionRate = amount
    this.saveSection.emit(this.section)
  }
  
  async changeSettings(event: MouseEvent) {
    const data: BudgetSettingsDialogData = {
      x: event.x - 350,
      y: event.y + 27,
      readOnly: this.readOnly,
      settings: this.section.settings,
      showFees: !this.public,
      showMarkup: !this.public && !this.isRevenue,
      showUnitRate: !this.public && !this.isRevenue,
      budgetCurrency: this.budgetCurrency,
      isShareView: this.public
    }
    
    const oldSettingsString = JSON.stringify(data.settings)
    console.log(data)

    const settings = await openDialogGetResult<SectionSettings>(this.dialog, BudgetSettingsComponent, {
      width: '400px',
      autoFocus: false,
      data,
      backdropClass: 'clear',
    })
    if (oldSettingsString !== JSON.stringify(settings)) {
      this.section.settings = settings
      this.saveSection.emit(this.section)
      this.setColumns()


      // only percents that have been changed should be updated as defaults.  That makes this logic very messy (they really should be set elsewhere in some account settings menu instead, but no)
      const partialSettings: Partial<SectionSettings> = {}
      const oldSettings = JSON.parse(oldSettingsString)
      accountBudgetRateKeys.forEach(k => {
        if (settings[k] !== oldSettings[k]) {
          (partialSettings as any)[k] = settings[k]
        }
      })
      // console.log({ oldSettings, settings })
      if (Object.keys(partialSettings).length) {
        console.log("Settings have changes", { partialSettings })
        this.changeSectionSettingDefaults.emit(partialSettings)
      }


    }
  }

  addLine() {
    this.section.lineItems.push(createDefaultLineItem())
    this.saveSection.emit(this.section)
  }

}

